import * as React from 'react';
import MuiDialog from '@mui/material/Dialog';
import { useBlocker, useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import * as Device from 'react-device-detect';
import { validate as isUuid } from 'uuid';

import { useStatusBarScrollToTop } from '@hooks';
import Spot from './spot';
import Event from './event';
import Consumer from './consumer';

type Type = 'consumer' | 'event' | 'spot';

interface EntityDialogProps {}

export function EntityDialog(props: Readonly<EntityDialogProps>) {
  const [open, setOpen] = React.useState(false);
  const [entity, setEntity] = React.useState<Type | null>(null);
  const [target, setTarget] = React.useState<string | null>(null);
  const resetScrollOnNextOpenRef = React.useRef(false);
  const pendingBackRef = React.useRef<null | { toRoot: boolean }>(null);
  const closingBecauseNavigationRef = React.useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Handle iOS status bar tap to scroll to top
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  useStatusBarScrollToTop(scrollRef);

  const blocker = useBlocker((params) => {
    const { currentLocation, nextLocation } = params;
    // console.log(`[{BLOCKER}]:`, { entity, target, currentLocation, nextLocation });
    if (!open) return false;

    const isBlocked =
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash;

    // console.log(`[{isBlocked}]:`, isBlocked);
    return isBlocked;
  });

  const handleBack = (toRoot = false) => {
    const currentPath = `${location.pathname}${location.search}`;
    const options = { preventScrollReset: true, replace: false, state: { from: currentPath } };
    const from = location.state?.from;
    const historyIdx = window.history.state?.idx ?? 0;
    const shouldGoRoot = historyIdx <= 0;
    // console.log(`[{handleBack}]:`, { location, from, shouldGoRoot, historyIdx, toRoot });

    let rootPath = '/';

    if (location.pathname.endsWith('/spots')) {
      rootPath = '/spots';
    } else if (location.pathname.startsWith('/me/calendar/event')) {
      rootPath = '/me/calendar/event';
    } else if (location.pathname.startsWith('/search')) {
      rootPath = '/search';
    } else if (searchParams.has('search')) {
      rootPath = '/search';
    }

    // console.log(`[{handleBack}]:`, { toRoot: toRoot === true, shouldGoRoot, rootPath, currentPath });

    if (toRoot === true || shouldGoRoot) {
      navigate(rootPath, options);
    } else if (!shouldGoRoot) {
      // @ts-expect-error ts(2345)
      navigate(-1, options);
    }

    return;
  };

  const handleClose = (e: React.SyntheticEvent, reason: string) => {
    pendingBackRef.current = {
      toRoot: ['backdropClick', 'escapeKeyDown'].includes(reason),
    };

    // trigger the slide-out animation (does not navigate yet)
    setOpen(false);
  };

  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      closingBecauseNavigationRef.current = true;
      resetScrollOnNextOpenRef.current = true;
      pendingBackRef.current = null; // priority to nav “forward”
      setOpen(false); // trigger the slide-out animation
    }
  }, [blocker.state]);

  React.useEffect(() => {
    if (!open) return;
    if (!resetScrollOnNextOpenRef.current) return;

    resetScrollOnNextOpenRef.current = false;

    const el = scrollRef.current;
    if (!el) return;

    el.scrollTo({ top: 0, behavior: 'instant' });
  }, [open, entity]);

  React.useEffect(() => {
    // search params have priority over url params as they are more explicit (can be set from anywhere in the app, not just from a link)
    const e = searchParams.get('e');
    const s = searchParams.get('s');
    const c = searchParams.get('c');
    // console.log('[QUERY]:', { e, s, c });

    if (!!e && isUuid(e ?? '')) {
      setEntity('event');
      setTarget(e);
    } else if (!!s) {
      setEntity('spot');
      setTarget(s);
    } else if (!!c) {
      setEntity('consumer');
      setTarget(c);
    } else {
      const { eventId = null, slug = null, memberSlug = null } = params;
      // console.log('[PARAMS]:', { slug, eventId, location: location.pathname });

      if (isUuid(eventId ?? '')) {
        // if (isUuid(eventId ?? '') && location.pathname.endsWith('/detail')) {
        setEntity('event');
        setTarget(eventId);
      } else if (slug || !!s) {
        // slug param is only available for spots
        setEntity('spot');
        setTarget(slug ?? s);
      } else if (memberSlug || !!c) {
        setEntity('consumer');
        setTarget(memberSlug ?? c);
      } else {
        setEntity(null);
        setTarget(null);
      }
    }
  }, [searchParams, params, location.pathname]);

  React.useEffect(() => {
    setOpen(Boolean(entity));
  }, [entity, target]);

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      fullWidth
      keepMounted
      fullScreen={Device.isMobile}
      maxWidth="md"
      scroll="body"
      slotProps={{
        transition: {
          onExited: () => {
            // console.log(`[{blocker.state}]:`, blocker.state);
            if (closingBecauseNavigationRef.current) {
              closingBecauseNavigationRef.current = false;

              if (blocker.state === 'blocked') {
                blocker.proceed(); // navigation “forward” plays AFTER navigation
                return;
              }
            }

            const pending = pendingBackRef.current;
            if (pending) {
              pendingBackRef.current = null;
              handleBack(pending.toRoot);
            }
          },
        },
        paper: {
          ref: scrollRef,
        },
      }}
    >
      {entity === 'consumer' && <Consumer handleBack={handleBack} />}
      {entity === 'event' && <Event handleBack={handleBack} />}
      {entity === 'spot' && <Spot handleBack={handleBack} />}
    </MuiDialog>
  );
}
